import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export type PageChrome = "public" | "app"

interface PageFrameProps {
  chrome: PageChrome
  children: ReactNode
  className?: string
}

/**
 * Symmetric page gutters. One definition, shared by PageFrame and the
 * dashboard layout so a page can't drift out of alignment with its neighbours.
 *
 * The phone value is 13.34px rather than a round 16: it is the gutter the
 * design works to, and Tailwind's px-4 was only ever the nearest step on the
 * scale.
 */
export const PAGE_GUTTER = "px-[13.34px] md:px-6 lg:px-8"

/**
 * Widescreen cap. Content centres and stops growing at 1600px — wide enough
 * that a dashboard fills a 27" monitor, bounded enough that a table row or a
 * paragraph doesn't run the full width of an ultrawide.
 */
export const PAGE_MAX = "mx-auto w-full max-w-[1600px]"

/**
 * Home-overview cap. The three home dashboards (owner, barber, client) read
 * as a narrower centred column — ~80% of the viewport on md+, still bounded
 * by the 1600px widescreen cap so ultrawides don't overshoot PAGE_MAX.
 * Mobile is untouched (phones already use the full width minus gutters).
 */
export const HOME_MAX = "mx-auto w-full md:max-w-[min(80svw,1600px)]"

/**
 * Bottom clearance for the FloatingNav launcher, which sits at bottom-6 and is
 * 64px tall (so it occupies 24–88px). Anything that scrolls to the bottom of
 * the page needs this or its last row ends up underneath the button.
 */
export const NAV_CLEARANCE = "pb-28"

/**
 * Horizontal alignment for content beneath the page chrome.
 *
 * Both modes are symmetric now. "app" used to be lopsided — md:pl-28 / lg:pl-32
 * against a md:pr-6 right edge — for one reason: DesktopFloatingNav pinned a
 * pill at the top-left and content had to clear it. The pill is gone, and with
 * it the reason for a left gutter five times the right one.
 *
 * What still separates the two modes is intent, not chrome: "public" marketing
 * pages bleed wide by design, "app" pages sit in a capped, centred column.
 */
export function PageFrame({ chrome, children, className }: PageFrameProps) {
  // Only the widescreen gutter here, never the mobile one.
  //
  // "app" used to apply the full PAGE_GUTTER (px-4 md:px-6), but every page
  // inside this frame — listings, hiring, community — already sets its own
  // px-4 on each section. Signed-in visitors were getting both: 32px of gutter
  // per side on a phone against 16px for logged-out visitors, which is why
  // Browse Shops and the Job Board read as too narrow once you signed in.
  //
  // Dashboards are unaffected: they import PAGE_GUTTER directly rather than
  // going through this component.
  // lg:px-4, halved from lg:px-8. These pages carry wide content — a map, a
  // listings grid — and 32px of desktop gutter was taking width the content
  // wanted. Dashboards are unaffected: they import PAGE_GUTTER directly.
  const chromeClass = chrome === "public" ? "lg:px-4" : cn("lg:px-4", PAGE_MAX)
  return <div className={cn(chromeClass, className)}>{children}</div>
}
